import { z } from 'zod';

/**
 * Fixed value sets — the only place they are defined (ADR 0010). The database stores the `code`
 * (text + CHECK), the API validates against these schemas, the UI renders `tone` and translates
 * `labelKey`. Never compare against raw strings elsewhere.
 */

export type Tone = 'neutral' | 'success' | 'info' | 'warning' | 'danger' | 'accent';

export interface EnumOption<C extends string> {
  readonly code: C;
  readonly labelKey: string;
  readonly tone: Tone;
}

const allOptions: EnumOption<string>[] = [];

/** Every option of every enum — the translation test (C8) checks each `labelKey` exists. */
export const ALL_ENUM_OPTIONS: readonly EnumOption<string>[] = allOptions;

/** `keyOf` — the translation key part of a code that is not a plain word (e.g. "9.1" → "9_1"). */
export function defineEnum<const C extends string>(
  prefix: string,
  options: readonly (readonly [C, Tone])[],
  keyOf: (code: C) => string = (code) => code,
) {
  const list: EnumOption<C>[] = options.map(([code, tone]) => ({
    code,
    tone,
    labelKey: `${prefix}.${keyOf(code)}`,
  }));
  allOptions.push(...list);
  const codes = list.map((o) => o.code) as [C, ...C[]];
  return {
    options: list as readonly EnumOption<C>[],
    codes: codes as readonly C[],
    schema: z.enum(codes),
    byCode: (code: C): EnumOption<C> => {
      const found = list.find((o) => o.code === code);
      if (!found) throw new Error(`Unknown ${prefix} code: ${code}`);
      return found;
    },
  };
}

export const Roles = defineEnum('enum.role', [
  ['owner', 'accent'],
  ['admin', 'danger'],
  ['manager', 'info'],
  ['member', 'success'],
  ['viewer', 'neutral'],
] as const);
export type Role = (typeof Roles.codes)[number];
export const roleSchema = Roles.schema;

/** Roles that can be granted through an invitation (ownership is transferred, not invited). */
export const INVITABLE_ROLES = ['admin', 'manager', 'member', 'viewer'] as const satisfies readonly Role[];
export const invitableRoleSchema = z.enum(INVITABLE_ROLES);
export type InvitableRole = (typeof INVITABLE_ROLES)[number];

/** PMAI customer statuses (FR-CUST-003); PMAI stored the Latvian label — mapped on import. */
export const CustomerStatuses = defineEnum('enum.customerStatus', [
  ['active_project', 'success'],
  ['active_support', 'info'],
  ['inactive', 'warning'],
  ['closed', 'danger'],
  ['in_question', 'accent'],
] as const);
export type CustomerStatus = (typeof CustomerStatuses.codes)[number];

/** Project status (FR-PRJ-003). PMAI codes `active-hot`/`on-hold` are mapped on import. */
export const ProjectStatuses = defineEnum('enum.projectStatus', [
  ['planning', 'info'],
  ['active', 'success'],
  ['active_hot', 'danger'],
  ['on_hold', 'warning'],
  ['completed', 'neutral'],
  ['cancelled', 'neutral'],
] as const);
export type ProjectStatus = (typeof ProjectStatuses.codes)[number];

/** Sort rank — ascending puts cancelled first, active-hot last (PMAI `statusOrder`, FR-DASH-007). */
export const PROJECT_STATUS_RANK: Readonly<Record<ProjectStatus, number>> = {
  cancelled: 1,
  completed: 2,
  on_hold: 3,
  planning: 4,
  active: 5,
  active_hot: 6,
};

/** Completed and cancelled projects are "closed" (task pickers, projects without tasks). */
export const isProjectOpen = (status: ProjectStatus): boolean =>
  status !== 'completed' && status !== 'cancelled';

/** Priority (FR-PRJ-003). */
export const Priorities = defineEnum('enum.priority', [
  ['low', 'neutral'],
  ['medium', 'info'],
  ['high', 'warning'],
  ['critical', 'danger'],
] as const);
export type Priority = (typeof Priorities.codes)[number];

export const PRIORITY_RANK: Readonly<Record<Priority, number>> = { low: 1, medium: 2, high: 3, critical: 4 };

/** Timesheet scenario: the plan and the actual (fact) sheet of a project (FR-TS-001). */
export const Scenarios = defineEnum('enum.scenario', [
  ['plan', 'info'],
  ['fact', 'success'],
] as const);
export type Scenario = (typeof Scenarios.codes)[number];

/** Review of a Jira worklog (FR-JIRA-019). */
export const ReviewStatuses = defineEnum('enum.reviewStatus', [
  ['pending', 'warning'],
  ['approved', 'success'],
  ['rejected', 'danger'],
] as const);
export type ReviewStatus = (typeof ReviewStatuses.codes)[number];

/** Support type (FR-SUP-004). */
export const SupportTypes = defineEnum('enum.supportType', [
  ['on_demand', 'neutral'],
  ['standard', 'info'],
  ['premium', 'accent'],
  ['other', 'neutral'],
] as const);
export type SupportType = (typeof SupportTypes.codes)[number];
